import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym, replaceTeamNames } from '../utils/teamAcronyms';

function formatDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  const day = d.getDate();
  const mon = d.toLocaleString('en', { month: 'short' });
  return `${day} ${mon}`;
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function fmtDiff(v) {
  if (v == null) return '—';
  const pct = (v * 100).toFixed(1);
  return v > 0 ? `+${pct}%` : `${pct}%`;
}

function diffColor(v) {
  if (v == null) return undefined;
  if (v > 0.01) return '#198754';
  if (v < -0.01) return '#dc3545';
  return '#6c757d';
}

const MARKET_TABS = [
  { key: 'matchOdds', label: 'Match Odds' },
  { key: 'asianHandicap', label: 'Asian Handicap' },
  { key: 'overUnder', label: 'Over/Under' },
];

export default function MatchOddsPricingCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate, modelDatesParam } = useDashboard();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMarket, setActiveMarket] = useState('matchOdds');

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ team: selectedTeam, date: selectedDate });
    if (modelDatesParam) params.set('modelDates', modelDatesParam);
    apiFetch(`/match-pricing?${params}`, token)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate, modelDatesParam]);

  const marketData = data?.markets?.[activeMarket];
  const hasMarketPrices = marketData?.selections?.some((s) => s.market != null);

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center">
        <strong>Match Pricing</strong>
        <span className="ms-auto text-muted small">Next match</span>
      </div>
      <div className="card-body p-0">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && data?.noMatch && (
          <div className="text-center py-4 text-muted small">No upcoming match within 7 days</div>
        )}
        {!loading && !error && data && !data.noMatch && data.noMatrix && (
          <div className="text-center py-4 text-muted small">No model prediction available</div>
        )}
        {!loading && !error && data && !data.noMatch && !data.noMatrix && (
          <>
            <div className="text-center pt-2 pb-1">
              <span className="small fw-semibold">{teamAcronym(data.home)} v {teamAcronym(data.away)}</span>
              <span className="text-muted small ms-2">{formatDate(data.matchDate)}</span>
            </div>

            <div className="d-flex justify-content-center gap-1 px-2 pb-2">
              {MARKET_TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`btn btn-sm ${activeMarket === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '0.7em', padding: '2px 8px' }}
                  onClick={() => setActiveMarket(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {marketData && (
              <div className="table-responsive">
                <table className="table table-sm mb-0 small text-center">
                  <thead>
                    <tr>
                      <th className="text-start ps-3" style={{ fontSize: '0.8em' }}>Selection</th>
                      <th style={{ fontSize: '0.8em' }}>Model</th>
                      {hasMarketPrices && <th style={{ fontSize: '0.8em' }}>Market</th>}
                      {hasMarketPrices && <th style={{ fontSize: '0.8em' }}>Diff</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {marketData.selections.map((sel) => (
                      <tr key={sel.name}>
                        <td className="text-start ps-3 text-nowrap" style={{ fontSize: '0.8em' }}>
                          {replaceTeamNames(sel.name)}
                        </td>
                        <td style={{ fontSize: '0.8em' }}>{fmtPct(sel.model)}</td>
                        {hasMarketPrices && (
                          <td style={{ fontSize: '0.8em' }}>{fmtPct(sel.market)}</td>
                        )}
                        {hasMarketPrices && (
                          <td
                            style={{ fontSize: '0.8em', color: diffColor(sel.diff), fontWeight: 600 }}
                          >
                            {fmtDiff(sel.diff)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data.oddsSource && (
              <div className="text-end px-3 pb-2">
                <span className="text-muted" style={{ fontSize: '0.65em' }}>
                  Market: {data.oddsSource}
                  {data.overround && ` (${((data.overround - 1) * 100).toFixed(1)}% o/r)`}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
